import * as React from "react";
import Grid from "@mui/material/Grid";
import {
  Typography,
  Box,
  Container,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Divider
} from "@mui/material";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MainInput, main_button, centered_flex_box } from "../../app/components/Styles";
import { getOrderForRTO, addInboundRTO } from "../../app/store/actions/imsAdminActions";
import moment from "moment";

export const CreateInboundRTO = ({ getOrderForRTO, addInboundRTO, order, orderItems, isLoading, user }) => {
  const navigate = useNavigate();
  const [trackingId, setTrackingId] = React.useState("");
  const [quantities, setQuantities] = React.useState({});
  const [hasFetched, setHasFetched] = React.useState(false);

  React.useEffect(() => {
    // Initialize quantities when orderItems are loaded
    if (orderItems && orderItems.length > 0 && !hasFetched) {
      const initialQuantities = {};
      orderItems.forEach((item) => {
        const product = item.product || item.stockItem?.product;
        initialQuantities[item._id] = product?.deletedFromShopify
          ? 0
          : item.quantityOrdered || item.quantity || 1;
      });
      setQuantities(initialQuantities);
      setHasFetched(true);
    }
  }, [orderItems, hasFetched]);

  const handleSearch = (event) => {
    event.preventDefault();
    if (!trackingId.trim()) {
      return;
    }
    setQuantities({});
    setHasFetched(false);
    getOrderForRTO({ trackingId: trackingId.trim() }, navigate);
  };

  const handleQuantityChange = (itemId, value) => {
    const numValue = parseInt(value) || 0;
    const orderItem = orderItems?.find((item) => item._id === itemId);
    const product = orderItem?.product || orderItem?.stockItem?.product;
    const maxQuantity = orderItem?.quantityOrdered || orderItem?.quantity || 1;

    if (product?.deletedFromShopify) {
      return;
    }
    
    if (numValue < 0) {
      setQuantities({ ...quantities, [itemId]: 0 });
    } else if (numValue > maxQuantity) {
      setQuantities({ ...quantities, [itemId]: maxQuantity });
    } else {
      setQuantities({ ...quantities, [itemId]: numValue });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    
    if (!order || !orderItems || orderItems.length === 0) {
      return;
    }

    // Build products array with only items that have quantity > 0
    const products = orderItems
      .filter((item) => {
        const qty = quantities[item._id] || 0;
        return qty > 0;
      })
      .map((item) => ({
        _id: item.product?._id || item.stockItem?.product,
        quantity: quantities[item._id] || 0
      }));

    if (products.length === 0) {
      return;
    }

    const data = {
      orderId: order._id,
      products
    };

    addInboundRTO(data, navigate);
  };

  const resetForm = () => {
    setTrackingId("");
    setQuantities({});
    setHasFetched(false);
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          paddingTop: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Box sx={{ width: "100%", mt: 2 }}>
          <Typography mt={2} variant="h4" textAlign={"center"} sx={{ color: "var(--secColor)" }}>
            Create Inbound RTO
          </Typography>

          {/* Search Form */}
          <Box component="form" onSubmit={handleSearch} sx={{ mt: 3, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={9}>
                <MainInput
                  margin="normal"
                  required
                  fullWidth
                  id="trackingId"
                  label="Tracking ID"
                  name="trackingId"
                  value={trackingId}
                  onChange={(e) => setTrackingId(e.target.value)}
                  autoFocus
                  placeholder="Enter order tracking ID"
                />
              </Grid>
              <Grid item xs={3}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, ...main_button }}
                  disabled={isLoading || !trackingId.trim()}
                >
                  {isLoading ? <CircularProgress sx={{ color: "white" }} size={24} /> : "Search"}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Order Information */}
          {order && (
            <>
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: "var(--secColor)" }}>
                    Order Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Order ID
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {order.externalId || order._id}
                      </Typography>
                    </Grid>
                    {order.shopifyOrderNumber && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Shopify Order
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {order.shopifyOrderNumber}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {order.status}
                      </Typography>
                    </Grid>
                    {order.bostaTrackingId && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Bosta Tracking ID
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {order.bostaTrackingId}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Created At
                      </Typography>
                      <Typography variant="body1">
                        {moment(order.createdAt).format("lll")}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Financial Status
                      </Typography>
                      <Typography variant="body1">
                        {order.financialStatus}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Order Items */}
              {orderItems && orderItems.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: "var(--secColor)" }}>
                      Order Items
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box component="form" onSubmit={handleSubmit}>
                      {orderItems.map((item) => {
                        const maxQuantity = item.quantityOrdered || item.quantity || 1;
                        const product = item.product || item.stockItem?.product;
                        const isDeleted = Boolean(product?.deletedFromShopify);
                        const currentQuantity = isDeleted ? 0 : quantities[item._id] || maxQuantity;

                        return (
                          <Card
                            key={item._id}
                            sx={{
                              mb: 2,
                              p: 2,
                              ...(isDeleted
                                ? { backgroundColor: "#ffe5e5", color: "#b71c1c" }
                                : {})
                            }}
                          >
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={2} sx={{ ...centered_flex_box }}>
                                {product?.image && (
                                  <Avatar
                                    src={product.image}
                                    sx={{ width: 80, height: 80 }}
                                    variant="rounded"
                                  />
                                )}
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="body1" fontWeight="bold">
                                  {`(${product?.sku || product?.externalId || "N/A"}) ${product?.name || "N/A"}`}
                                </Typography>
                                {product?.size && (
                                  <Typography variant="body2">Size: {product.size}</Typography>
                                )}
                                {product?.color && (
                                  <Typography variant="body2">Color: {product.color}</Typography>
                                )}
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  Price: {item.price} EGP
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Ordered Quantity: {maxQuantity}
                                </Typography>
                                {isDeleted && (
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    Deleted from Shopify – cannot be processed
                                  </Typography>
                                )}
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <MainInput
                                  margin="normal"
                                  fullWidth
                                  label="Return Quantity"
                                  type="number"
                                  inputProps={{
                                    min: 0,
                                    max: maxQuantity
                                  }}
                                  value={currentQuantity}
                                  onChange={(e) => handleQuantityChange(item._id, e.target.value)}
                                  helperText={
                                    isDeleted ? "This product was deleted from Shopify" : `Max: ${maxQuantity}`
                                  }
                                  disabled={isDeleted}
                                />
                              </Grid>
                            </Grid>
                          </Card>
                        );
                      })}

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2, ...main_button }}
                        disabled={isLoading || orderItems.length === 0}
                      >
                        {isLoading ? (
                          <CircularProgress sx={{ color: "white" }} />
                        ) : (
                          "Create Inbound RTO"
                        )}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Reset Button */}
              <Box sx={{ ...centered_flex_box, mb: 3 }}>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  sx={{ ...main_button }}
                >
                  Search Another Order
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading || state?.wait?.isLoading,
  order: state?.records?.order,
  orderItems: state?.records?.orderItems
});

const mapDispatchToProps = { getOrderForRTO, addInboundRTO };

export default connect(mapStateToProps, mapDispatchToProps)(CreateInboundRTO);

